import { useState } from 'react';
import type { Loan, LoanEvent, PrepaymentEvent, RepaymentConsequence } from '@/types';
import { RepaymentConsequenceFields } from './RepaymentConsequenceFields';

type PrepaymentConsequence = NonNullable<PrepaymentEvent['repaymentConsequence']>;

interface PrepaymentConsequenceFieldsProps {
  loan: Loan;
  events: LoanEvent[];
  eventEffectiveDate: string;
  /**
   * The prepayment amount the user has entered on the parent form — used
   * to reduce the basis principal by exactly this much (see
   * RepaymentConsequenceFields' principalAdjustment prop), since the
   * prepayment event itself hasn't been saved yet and therefore isn't
   * part of `events`.
   */
  prepaymentAmount: number;
  value: PrepaymentConsequence | undefined;
  onChange: (next: PrepaymentConsequence) => void;
}

/**
 * Thin wrapper around the shared RepaymentConsequenceFields, adding the
 * `intent` field specific to PrepaymentEvent (see PrepaymentEvent in
 * src/types/events.ts). The shared component does all the actual
 * EMI<->tenure calculation, basis derivation, and no-ping-pong logic;
 * this wrapper only adds the intent selector and passes
 * principalAdjustment = -prepaymentAmount so the suggestion reflects
 * the principal AFTER this specific reduction, per the approved spec
 * ("The engine calculates the suggested revised EMI/tenure after the
 * principal reduction").
 */
export function PrepaymentConsequenceFields({
  loan,
  events,
  eventEffectiveDate,
  prepaymentAmount,
  value,
  onChange,
}: PrepaymentConsequenceFieldsProps) {
  const [intent, setIntent] = useState<PrepaymentConsequence['intent']>(value?.intent ?? 'pending_lic_action');

  function handleSharedChange(next: RepaymentConsequence) {
    onChange({ ...next, intent });
  }

  function handleIntentChange(next: PrepaymentConsequence['intent']) {
    setIntent(next);
    if (value) {
      onChange({ ...value, intent: next });
    }
  }

  return (
    <div className="stack">
      <div className="field-group">
        <label htmlFor="prepaymentIntent">Intent</label>
        <select
          id="prepaymentIntent"
          value={intent}
          onChange={(e) => handleIntentChange(e.target.value as PrepaymentConsequence['intent'])}
        >
          <option value="reduce_tenure">Reduce Tenure (keep EMI the same)</option>
          <option value="reduce_emi">Reduce EMI (keep tenure the same)</option>
          <option value="both">Both EMI and Tenure Change</option>
          <option value="pending_lic_action">Pending LIC Action (not yet known)</option>
        </select>
      </div>

      <RepaymentConsequenceFields
        loan={loan}
        events={events}
        eventEffectiveDate={eventEffectiveDate}
        principalAdjustment={-prepaymentAmount}
        value={value}
        onChange={handleSharedChange}
      />
    </div>
  );
}
